import { useState } from "react";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { requestMagicLink } from "../lib/api";

// Inline sign-in prompt shown on a visitor's SECOND check in the same session.
// Reuses the existing magic-link endpoint — no new auth surface.
export default function SecondCheckSignIn({ onSent }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    const e = email.trim().toLowerCase();
    if (!e || busy) return;
    setBusy(true);
    try {
      await requestMagicLink(e);
      setSent(true);
      onSent?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        toast.message("Sign-in email is warming up", {
          description: "Please try again in a moment.",
        });
      } else {
        toast.error("Couldn't send your link — please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="second-check-signin"
      className="rounded-2xl border border-surface-line bg-white px-5 py-6 max-w-md mx-auto"
    >
      {sent ? (
        <div data-testid="second-check-signin-sent" className="flex items-start gap-3">
          <Check className="w-5 h-5 text-safe-deep shrink-0 mt-0.5" strokeWidth={2.5} />
          <p className="text-[14px] text-ink leading-relaxed">
            Check your inbox — we sent a one-tap sign-in link.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-cream-100 border border-surface-line text-ink-soft shrink-0">
              <Mail className="w-4 h-4" />
            </span>
            <h3 className="text-[16px] font-bold text-ink">Keep checking — free</h3>
          </div>
          <p className="mt-2 text-[13.5px] text-ink-soft leading-relaxed">
            Sign in with your email to run up to 3 checks a month. No password.
          </p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              data-testid="second-check-signin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="you@email.com"
              className="flex-1 h-11 px-3.5 rounded-xl border border-surface-line bg-cream-50 text-[14px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-2 focus:ring-peach-100"
            />
            <button
              type="button"
              data-testid="second-check-signin-submit"
              onClick={submit}
              disabled={busy || !email.trim()}
              className="h-11 px-5 rounded-xl bg-ink text-cream-50 hover:bg-ink-soft text-[14px] font-bold transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {busy ? "Sending…" : "Send my sign-in link"}
            </button>
          </div>
          <p className="mt-2.5 text-[12px] text-ink-mute">Already have Pro? Same email works.</p>
        </>
      )}
    </div>
  );
}
